import { Controller } from "@hotwired/stimulus"
import Sortable from "sortablejs"

// A custom's media board on its edit page (custom_figures/_media): every
// [data-zone] is a drop list sharing one Sortable group. The library lends
// copies (its tiles stay put); the gallery and journal entries hand their
// tiles over; the main picture only takes (its item leaves by the library
// or by a swap). Each drop is
// sent to the server -- photos#place, or a new journal entry -- whose
// turbo-stream answer re-renders the whole board.
export default class extends Controller {
  static targets = ["library", "inspector", "uploadForm", "fileInput", "progress", "dialog", "landing"]
  static values = { photosUrl: String, entriesUrl: String, gallerySlots: Number, selected: String }

  connect() {
    this.sortables = [...this.element.querySelectorAll("[data-zone]")].map((zone) => this.sortable(zone))
    if (this.selectedValue) this.show(this.selectedValue)
    this.element.addEventListener("input", this.mirrorCaption)
  }

  disconnect() {
    this.sortables.forEach((s) => s.destroy())
    this.element.removeEventListener("input", this.mirrorCaption)
  }

  sortable(zone) {
    const kind = zone.dataset.zone
    return new Sortable(zone, {
      group: {
        name: "media",
        pull: kind === "library" ? "clone" : kind !== "main" && kind !== "new-entry",
        put: (to, from) => this.accepts(kind, zone, from)
      },
      sort: kind === "gallery" || kind === "journal",
      draggable: ".mx-tile",
      animation: 150,
      ghostClass: "mx-ghost",
      onAdd: (event) => this.dropped(event),
      onUpdate: (event) => this.dropped(event)
    })
  }

  // Which drops a place takes: the gallery up to its slots, the library
  // only what's placed; the main picture and journal entries anything.
  accepts(kind, zone, from) {
    const fromZone = from.el.dataset.zone
    if (kind === "gallery") return fromZone === "gallery" || zone.querySelectorAll(".mx-tile").length < this.gallerySlotsValue
    if (kind === "library") return fromZone !== "library"
    return true
  }

  dropped(event) {
    const zone = event.to
    const id = event.item.dataset.mediaId
    const to = zone.dataset.zone
    if (to === "library" && event.from === event.to) return
    if (to === "new-entry") return this.send(this.entriesUrlValue, "POST", { media_id: id })
    const order = [...zone.querySelectorAll(".mx-tile")].map((tile) => tile.dataset.mediaId)
    this.send(`${this.photosUrlValue}/${id}/place`, "PATCH", { to, entry_id: zone.dataset.entryId || "", order })
  }

  newEntry(event) {
    if (event.target.closest(".mx-tile")) return
    this.send(this.entriesUrlValue, "POST", {})
  }

  async send(url, method, params) {
    const body = new FormData()
    Object.entries(params).forEach(([key, value]) => {
      if (Array.isArray(value)) value.forEach((v) => body.append(`${key}[]`, v))
      else body.append(key, value)
    })
    this.element.classList.add("is-busy")
    const response = await fetch(url, {
      method, body,
      headers: { Accept: "text/vnd.turbo-stream.html", "X-CSRF-Token": document.querySelector("meta[name=csrf-token]")?.content }
    })
    if (response.ok) window.Turbo.renderStreamMessage(await response.text())
    else this.element.classList.remove("is-busy")
  }

  // Clicking a tile (not dragging it) opens it in the inspector.
  select(event) {
    const tile = event.currentTarget
    this.show(tile.dataset.mediaId)
  }

  show(id) {
    this.element.querySelectorAll(".mx-tile").forEach((t) => t.classList.toggle("is-on", t.dataset.mediaId === String(id)))
    this.inspectorTarget.querySelectorAll("[data-media-panel]").forEach((panel) => { panel.hidden = panel.dataset.mediaPanel !== String(id) })
    this.inspectorTarget.classList.toggle("has-pick", !!this.inspectorTarget.querySelector("[data-media-panel]:not([hidden])"))
  }

  // A caption typed in the inspector shows under its tiles straight away.
  mirrorCaption = (event) => {
    const id = event.target.dataset?.captionInput
    if (!id) return
    this.element.querySelectorAll(`[data-caption-for="${id}"]`).forEach((line) => {
      line.textContent = event.target.value || "No caption"
      line.classList.toggle("is-empty", !event.target.value)
    })
  }

  filter(event) {
    const unplaced = event.currentTarget.dataset.filter === "unplaced"
    this.libraryTarget.classList.toggle("only-unplaced", unplaced)
    this.element.querySelectorAll(".mx-filter").forEach((b) => b.classList.toggle("on", b === event.currentTarget))
  }

  // Uploading: the Upload button opens a dialog whose landing area takes a
  // click (the file picker), dropped files, or a paste. Files go straight
  // up by direct upload, then into the library; the board that comes back
  // replaces this one, dialog and all. Files dropped on the library upload
  // the same way.
  openUpload() {
    this.dialogTarget.showModal()
    this.onPaste = (event) => {
      const files = [...(event.clipboardData?.files || [])]
      if (!files.length) return
      event.preventDefault()
      this.uploadFiles(files)
    }
    document.addEventListener("paste", this.onPaste)
    this.dialogTarget.addEventListener("close", () => document.removeEventListener("paste", this.onPaste), { once: true })
  }

  closeUpload() {
    if (!this.uploading) this.dialogTarget.close()
  }

  // A click on the backdrop (the dialog element itself, outside its box) closes it.
  dialogBackdrop(event) {
    if (event.target === this.dialogTarget) this.closeUpload()
  }

  upload() {
    if (!this.fileInputTarget.files.length || this.uploading) return
    this.uploading = true
    if (!this.dialogTarget.open) this.dialogTarget.showModal()
    this.landingTarget.classList.add("is-busy")
    const count = this.fileInputTarget.files.length
    this.progressTarget.textContent = `Uploading ${count} ${count === 1 ? "file" : "files"}…`
    this.fileInputTarget.addEventListener("direct-upload:progress", (e) => {
      this.progressTarget.textContent = `Uploading… ${Math.round(e.detail.progress)}%`
    })
    this.uploadFormTarget.requestSubmit()
  }

  uploadFiles(files) {
    const list = new DataTransfer()
    files.filter((file) => /^(image|video)\//.test(file.type)).forEach((file) => list.items.add(file))
    if (!list.files.length) return
    this.fileInputTarget.files = list.files
    this.upload()
  }

  landingOver(event) {
    if (![...event.dataTransfer.types].includes("Files")) return
    event.preventDefault()
    this.landingTarget.classList.add("is-over")
  }

  landingLeave() {
    this.landingTarget.classList.remove("is-over")
  }

  landingDrop(event) {
    if (!event.dataTransfer.files.length) return
    event.preventDefault()
    this.landingTarget.classList.remove("is-over")
    this.uploadFiles([...event.dataTransfer.files])
  }

  fileOver(event) {
    if (![...event.dataTransfer.types].includes("Files")) return
    event.preventDefault()
    this.libraryTarget.classList.add("is-file-over")
  }

  fileLeave() {
    this.libraryTarget.classList.remove("is-file-over")
  }

  fileDrop(event) {
    if (!event.dataTransfer.files.length) return
    event.preventDefault()
    this.libraryTarget.classList.remove("is-file-over")
    this.uploadFiles([...event.dataTransfer.files])
  }
}
